import { Suspense, useEffect, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { loginUrl, PROVIDERS, useProfile } from "../lib/auth";
import { useChat } from "../lib/chat";
import { lazyRoute } from "../lib/lazyRoute";
import { Caveat } from "./ui";

// The markdown renderer is a sizeable dependency that the lazy routes already
// pay for on demand. Loading it here eagerly would put it back into the
// landing chunk for every visitor, open dock or not — so it arrives with the
// first rendered answer instead. lazyRoute (rather than lazy) for the same
// reason as in main.tsx: chunk names change on deploy.
const Markdown = lazyRoute(() => import("./Markdown").then((m) => ({ default: m.Markdown })));

const SUGGESTIONS = [
  "Why is the top-ranked name where it is?",
  "What does the risk sleeve measure?",
  "Which themes are moving scores right now?",
  "What could not be measured for TSLA?",
];

/**
 * The assistant, on every route.
 *
 * Mounted once in the layout shell, so the conversation survives navigation
 * the way the Disclaimer does — and because it lives inside the router, it
 * knows which page a question was asked from and sends that along as context.
 * The transcript itself lives server-side; this widget only renders it.
 */
export function ChatDock() {
  const { profile, loading: profileLoading } = useProfile();
  const location = useLocation();
  const { turns, busy, historyState, activate, send, reset } = useChat(profile);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const launcherRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // The page the user is looking at, resolved to something retrieval can use.
  const symbol = matchPath("/ticker/:symbol", location.pathname)?.params.symbol;
  const pageContext = { route: location.pathname, ...(symbol ? { symbol } : {}) };

  useEffect(() => {
    if (open && profile) void activate();
  }, [open, profile, activate]);

  // Focus follows open state after render: the textarea when the panel
  // appears, the launcher when it goes away (the launcher does not exist in
  // the DOM until the panel unmounts, so this cannot happen inside close()).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) inputRef.current?.focus();
    else if (wasOpen.current) launcherRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  // Keep the newest turn in view as answers stream in.
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [turns, busy, open]);

  function close() {
    setOpen(false);
  }

  if (!open) {
    return (
      <button
        ref={launcherRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        aria-controls="chat-dock-panel"
        className="fixed bottom-4 right-4 z-30 rounded-full border px-4 py-2.5 text-sm font-medium shadow-lg"
        style={{
          background: "var(--surface-1)",
          borderColor: "var(--border-strong)",
          color: "var(--text-primary)",
        }}
      >
        Ask
      </button>
    );
  }

  const notApproved = historyState === "not_approved";

  return (
    <div
      id="chat-dock-panel"
      role="dialog"
      aria-label="Ask about the data"
      onKeyDown={(e) => {
        if (e.key === "Escape") close();
      }}
      className="fixed inset-0 z-30 flex flex-col border shadow-xl sm:inset-auto sm:bottom-4 sm:right-4 sm:w-[380px] sm:max-w-[calc(100vw-2rem)] sm:h-[min(560px,calc(100vh-6rem))] sm:rounded-lg"
      style={{ background: "var(--surface-1)", borderColor: "var(--border-strong)" }}
    >
      <header
        className="flex items-center justify-between gap-2 border-b px-3 py-2"
        style={{ borderColor: "var(--border)" }}
      >
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">Ask about the data</h2>
          <p className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>
            Answers come only from the nightly snapshot.
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {turns.length > 0 && (
            <button
              type="button"
              onClick={() => void reset()}
              className="rounded border px-2 py-1 text-xs"
              style={{ borderColor: "var(--border-strong)", color: "var(--text-secondary)" }}
            >
              New conversation
            </button>
          )}
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="rounded px-2 py-1 text-sm"
            style={{ color: "var(--text-secondary)" }}
          >
            ✕
          </button>
        </div>
      </header>

      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3">
        {!profileLoading && !profile && (
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            You are not signed in.{" "}
            {PROVIDERS.map((p, n) => (
              <span key={p.id}>
                {n > 0 && " or "}
                <a
                  href={loginUrl(p.id, window.location.href)}
                  className="underline underline-offset-2"
                  style={{ color: "var(--series-1)" }}
                >
                  Sign in with {p.label}
                </a>
              </span>
            ))}
            . A profile is created automatically the first time.
          </p>
        )}

        {notApproved && (
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            You are signed in, but this account is not approved for the assistant. Each question
            calls a paid model, so access is limited to specific accounts rather than to anyone who
            signs in. Everything else on the site is open to you.
          </p>
        )}

        {historyState === "loading" && turns.length === 0 && (
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Loading your conversation…
          </p>
        )}

        {profile && !notApproved && turns.length === 0 && historyState !== "loading" && (
          <div>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              This answers questions about what was measured — factor values, risk flags, themes,
              and what could not be computed. It will not tell you what to buy, and it has nothing
              useful to say about that: the ranking did not predict returns when tested.
            </p>
            <div className="flex flex-wrap gap-2 pt-3">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => void send(s, pageContext)}
                  className="rounded border px-2.5 py-1 text-xs text-left"
                  style={{ borderColor: "var(--border-strong)", color: "var(--text-secondary)" }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <ul className="space-y-4">
          {turns.map((turn, i) => (
            <li key={i}>
              <p className="text-sm font-medium">{turn.question}</p>
              {turn.answer === null && turn.error === null && (
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                  Thinking…
                </p>
              )}
              {turn.error === "SIGN_IN_REQUIRED" ? (
                <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                  You need to sign in first.{" "}
                  {PROVIDERS.map((p, n) => (
                    <span key={p.id}>
                      {n > 0 && " or "}
                      <a
                        href={loginUrl(p.id, window.location.href)}
                        className="underline underline-offset-2"
                        style={{ color: "var(--series-1)" }}
                      >
                        {p.label}
                      </a>
                    </span>
                  ))}
                  , then ask again.
                </p>
              ) : turn.error === "NOT_APPROVED" ? (
                <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                  You are signed in, but this account is not approved for the assistant. Each
                  question calls a paid model, so access is limited to specific accounts rather
                  than to anyone who signs in. Everything else on the site is open to you.
                </p>
              ) : (
                turn.error && (
                  <p className="text-sm mt-1" style={{ color: "var(--status-critical)" }}>
                    {turn.error}
                  </p>
                )
              )}
              {turn.answer && (
                <>
                  <div className="mt-1">
                    <Suspense
                      fallback={
                        <p className="text-sm whitespace-pre-wrap">{turn.answer}</p>
                      }
                    >
                      <Markdown>{turn.answer}</Markdown>
                    </Suspense>
                  </div>
                  {turn.groundedIn &&
                    (turn.groundedIn.symbols.length > 0 || turn.groundedIn.themes.length > 0) && (
                      <p className="text-[11px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                        Grounded in:{" "}
                        {[...turn.groundedIn.symbols, ...turn.groundedIn.themes].join(", ")}
                      </p>
                    )}
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="border-t px-3 pb-3 pt-2" style={{ borderColor: "var(--border)" }}>
        {profile && !notApproved && (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setDraft("");
              void send(draft, pageContext);
            }}
          >
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  setDraft("");
                  void send(draft, pageContext);
                }
              }}
              rows={2}
              maxLength={1000}
              placeholder="Ask about a company, a factor, or a theme…"
              className="flex-1 rounded border px-2 py-1.5 text-sm resize-y"
              style={{
                background: "var(--surface-0)",
                borderColor: "var(--border-strong)",
                color: "var(--text-primary)",
              }}
            />
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              className="self-end rounded border px-3 py-1.5 text-sm disabled:opacity-50"
              style={{ borderColor: "var(--border-strong)", color: "var(--text-primary)" }}
            >
              {busy ? "…" : "Ask"}
            </button>
          </form>
        )}

        <Caveat>
          Only approved accounts can use it — the endpoint calls a paid model. Every answer is
          restricted to the snapshot, so &ldquo;that is not in the data&rdquo; is a real and common
          answer rather than a failure.
        </Caveat>
      </div>
    </div>
  );
}
